import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CheckSquare,
  AlertTriangle,
  CheckCircle,
  XCircle,
  CreditCard,
  Building2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelApprovalInbox = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [actionComments, setActionComments] = useState('');
  const [assignedCardId, setAssignedCardId] = useState('');

  useEffect(() => {
    fetchApprovalRequests();
    fetchCreditCards();
  }, []);

  const fetchApprovalRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true });
      setRequests(data.requests || []);
    } catch (err) {
      toast.error('Failed to load approval inbox');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditCards = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards`, { withCredentials: true });
      setCreditCards(data.credit_cards || []);
      if (data.credit_cards?.length > 0) {
        setAssignedCardId(data.credit_cards[0]._id);
      }
    } catch (err) {}
  };

  const handleProcessAction = async (reqId, action) => {
    try {
      await axios.post(
        `${API_URL}/travel-desk/requests/${reqId}/action`,
        {
          action,
          comments: actionComments,
          assigned_card_id: assignedCardId,
        },
        { withCredentials: true }
      );
      toast.success(`Request ${action}d successfully!`);
      setActionComments('');
      fetchApprovalRequests();
    } catch (err) {
      toast.error(`Failed to ${action} request`);
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (activeTab === 'pending') return ['submitted', 'pending_approval'].includes(r.status);
    if (activeTab === 'approved') return ['approved', 'booked'].includes(r.status);
    if (activeTab === 'rejected') return r.status === 'rejected';
    if (activeTab === 'returned') return r.status === 'returned_for_correction';
    return true; // history
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <CheckSquare className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Travel Approval Inbox</h1>
            <p className="text-xs text-blue-200">
              Multi-level corporate travel approval matrix · Manager &amp; Finance Review
            </p>
          </div>
        </div>

        <button onClick={fetchApprovalRequests} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex gap-2">
        {['pending', 'approved', 'rejected', 'returned', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all ${
              activeTab === tab ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab} Requests
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
        {loading ? (
          <div className="p-8 text-center text-slate-500 font-medium">Loading approval inbox...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">
            No {activeTab} travel requests found in inbox.
          </div>
        ) : (
          filteredRequests.map((r) => (
            <div key={r._id} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-mono text-xs font-bold text-blue-600">{r.request_number}</span>
                  <h4 className="font-bold text-slate-900 text-base">{r.employee_name} — {r.purpose}</h4>
                </div>
                <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full uppercase">
                  Awaiting: {r.current_approval_step?.replace(/_/g, ' ')}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-white p-3.5 rounded-xl border border-slate-100 font-medium">
                <div><b>Department:</b> {r.department}</div>
                <div><b>Travel Mode:</b> <span className="capitalize">{r.travel_type}</span></div>
                <div><b>Route:</b> {r.journey_details?.[0]?.from_city} &rarr; {r.journey_details?.[0]?.to_city}</div>
                <div><b>Est. Budget:</b> ₹ {(r.estimated_budget || 0).toLocaleString('en-IN')}</div>
              </div>

              {r.current_approval_step === 'finance' && activeTab === 'pending' && (
                <div className="bg-indigo-50 border border-indigo-200 p-3.5 rounded-xl space-y-2 text-xs">
                  <div className="font-bold text-indigo-900">Finance Corporate Credit Card Assignment</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-600">Select Credit Card</label>
                      <select
                        value={assignedCardId}
                        onChange={(e) => setAssignedCardId(e.target.value)}
                        className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs"
                      >
                        {creditCards.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.card_name} ({c.card_number_masked})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'pending' && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-200">
                  <input
                    type="text"
                    placeholder="Approver remarks / comments..."
                    value={actionComments}
                    onChange={(e) => setActionComments(e.target.value)}
                    className="h-9 px-3 bg-white border border-slate-300 rounded-xl text-xs w-full max-w-md outline-none"
                  />

                  {hasPermission(user, 'travel_desk.approval.approve') ? (
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => handleProcessAction(r._id, 'reject')}
                        className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleProcessAction(r._id, 'approve')}
                        className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-md"
                      >
                        Approve Request &rarr;
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 font-semibold italic">View Only (Approve permission required)</span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TravelApprovalInbox;
